<template>
    <!-- Shimmer loading state -->
    <div class="featured-carousel shimmer-carousel" v-if="loading">
        <div class="shimmer-slide">
            <div class="shimmer-slide-bg"><div class="shimmer-overlay"></div></div>
            <div class="shimmer-slide-content">
                <div class="shimmer-badge"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-channel-row">
                    <div class="shimmer-ch-avatar"><div class="shimmer-overlay"></div></div>
                    <div class="shimmer-ch-name"><div class="shimmer-overlay"></div></div>
                </div>
                <div class="shimmer-title-line"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-title-line short"><div class="shimmer-overlay"></div></div>
                <div class="shimmer-btn"><div class="shimmer-overlay"></div></div>
            </div>
        </div>
    </div>

    <!-- Actual carousel -->
    <div class="featured-carousel" v-if="!loading && slides.length > 0" @mouseenter="pause" @mouseleave="resume">
        <div class="carousel-track" :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
            <div class="carousel-slide" v-for="slide in slides" :key="slide.id">
                <div class="slide-bg" :style="backgroundStyle(slide)"></div>
                <div class="slide-overlay"></div>
                <div class="slide-content">
                    <div class="slide-badge" v-if="slide._onThisDay || slide._nearDate">
                        <vue-feather type="calendar" size="13"></vue-feather>
                        {{ onThisDayLabel(slide) }}
                    </div>
                    <div class="slide-channel" v-if="slide.owner">
                        <img
                            :src="ownerAvatar(slide.owner)"
                            class="slide-channel-avatar"
                            @error="onAvatarError"
                        />
                        <span>{{ slide.owner.displayName || slide.owner.username }}</span>
                        <vue-feather v-if="slide.owner.status === 'verified'" type="check-circle" class="verified-badge-inline" size="14" stroke-width="2"></vue-feather>
                    </div>
                    <h2 class="slide-title">{{ slideTitle(slide) }}</h2>
                    <router-link :to="`/watch/${slide.id}`" class="slide-play-btn">
                        <vue-feather type="play" size="20" fill="currentColor"></vue-feather>
                        {{ currentLanguage === 'TA' ? 'இப்போது பாருங்கள்' : 'Watch Now' }}
                    </router-link>
                    <div class="slide-stats">
                        <span class="slide-stat">
                            <vue-feather type="eye" size="14"></vue-feather>
                            {{ formatViews(slide.viewsCount) }}
                        </span>
                        <span class="slide-stat">
                            <vue-feather type="thumbs-up" size="14"></vue-feather>
                            {{ formatLikes(slide.likesCount) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <button class="carousel-prev" @click="prev" aria-label="Previous slide">&#8249;</button>
        <button class="carousel-next" @click="next" aria-label="Next slide">&#8250;</button>
        <div class="carousel-dots">
            <button
                v-for="(s, i) in slides"
                :key="i"
                :class="['carousel-dot', { active: i === currentIndex }]"
                @click="goTo(i)"
                :aria-label="`Go to slide ${i + 1}`"
            ></button>
        </div>
    </div>
</template>

<script>
import { API } from 'aws-amplify';
import { listPublishedFeaturedVideos, videosByStatusWithOwner, topVideosByStatus } from '../graphql/custom.js';

export default {
    name: 'FeaturedCarousel',
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' },
        currentUserRef: { from: 'currentUserRef', default: null },
        isAdminRef: { from: 'isAdminRef', default: null }
    },
    data() {
        return {
            slides: [],
            currentIndex: 0,
            autoplayTimer: null,
            loading: true
        };
    },
    computed: {
        currentLanguage() {
            return this.languageRef || 'EN';
        },
        isAdminViewer() {
            // unwrapInjectedRef is on (main.js), so the injected ref arrives unwrapped.
            return this.isAdminRef === true;
        },
        myOwnerId() {
            const user = this.currentUserRef;
            return user?.model?.id || user?.sub || null;
        },
        // Changes once auth resolves (async, after created); used to re-fetch so
        // owners/admins get their draft significant videos included.
        authKey() {
            return `${this.isAdminViewer}|${this.myOwnerId || ''}`;
        }
    },
    created() {
        this.fetchSlides();
    },
    watch: {
        authKey() {
            this.fetchSlides();
        }
    },
    beforeUnmount() {
        this.clearTimer();
    },
    methods: {
        async fetchSlides() {
            this.loading = true;
            try {
                const now = new Date();
                const todayMonth = String(now.getMonth() + 1).padStart(2, '0');
                const todayDay = String(now.getDate()).padStart(2, '0');

                // Fill toward a full carousel, not just a minimum of 3.
                const TARGET_SLIDES = 8;
                const selected = [];

                // Significant-date videos: exact month+day matches in any year
                // lead, then any video whose significantDate falls within a
                // window of days around today, nearest first — year-agnostic and
                // wrapping across month/year boundaries, so in early September a
                // late-August anniversary still surfaces. (significantDate is
                // unindexed and sparse, so we pull them all once and match
                // locally.)
                try {
                    const significant = await this.fetchSignificantVideos();
                    const NEAR_WINDOW_DAYS = 30;
                    const todayDoy = this.dayOfYear(now.getMonth() + 1, now.getDate());
                    const onThisDay = [];
                    const nearby = [];
                    significant.forEach(v => {
                        const [, m, d] = (v.significantDate || '').split('-');
                        const month = parseInt(m, 10);
                        const day = parseInt(d, 10);
                        if (!month || !day) return;
                        if (m === todayMonth && d === todayDay) {
                            onThisDay.push({ ...v, _onThisDay: true });
                            return;
                        }
                        // Forward gap from today to the anniversary on a
                        // 365-day circle; the shorter arc is the distance.
                        const ahead = (this.dayOfYear(month, day) - todayDoy + 365) % 365;
                        const dist = Math.min(ahead, 365 - ahead);
                        if (dist <= NEAR_WINDOW_DAYS) {
                            nearby.push({ ...v, _nearDate: true, _dateDist: dist, _upcoming: ahead === dist });
                        }
                    });
                    // Nearest first; on a tie, an upcoming date ranks before one
                    // already passed.
                    nearby.sort((a, b) => {
                        if (a._dateDist !== b._dateDist) return a._dateDist - b._dateDist;
                        return (a._upcoming ? 0 : 1) - (b._upcoming ? 0 : 1);
                    });
                    selected.push(...onThisDay, ...nearby);
                } catch (e) {
                    console.error('FeaturedCarousel: error fetching significant-date videos', e);
                }

                // Fallback: explicitly selected featured videos. These can be older
                // than the first page of recent videos, so query them directly.
                if (selected.length < TARGET_SLIDES) {
                    try {
                        // isFeatured is unindexed; over a draft-heavy table a small
                        // limit rarely surfaces the few featured rows, so scan wider.
                        const featuredRes = await API.graphql({
                            query: listPublishedFeaturedVideos,
                            variables: {
                                filter: {
                                    status: { eq: 'ready' },
                                    publishState: { eq: 'published' },
                                    isFeatured: { eq: true }
                                },
                                limit: 500
                            }
                        });
                        const featured = (featuredRes.data.listVideoModels.items || [])
                            .filter(v => !selected.find(s => s.id === v.id))
                            .map(v => ({ ...v, _onThisDay: false }));
                        selected.push(...featured);
                    } catch (e) {
                        console.error('FeaturedCarousel: error fetching featured videos', e);
                    }
                }

                // Fallback: top viewed videos
                if (selected.length < TARGET_SLIDES) {
                    try {
                        const topRes = await API.graphql({
                            query: topVideosByStatus,
                            variables: {
                                status: 'ready',
                                sortDirection: 'DESC',
                                viewsCount: { gt: 0 },
                                filter: { publishState: { eq: 'published' } },
                                limit: 60
                            }
                        });
                        const topVideos = (topRes.data.topVideosByStatus.items || [])
                            .filter(v => !selected.find(s => s.id === v.id))
                            .map(v => ({ ...v, _onThisDay: false }));
                        selected.push(...topVideos);
                    } catch (e) {
                        console.error('FeaturedCarousel: error fetching top videos fallback', e);
                    }
                }

                if (selected.length < TARGET_SLIDES) {
                    try {
                        const recentRes = await API.graphql({
                            query: videosByStatusWithOwner,
                            variables: {
                                status: 'ready',
                                sortDirection: 'DESC',
                                filter: { publishState: { eq: 'published' } },
                                limit: 40
                            }
                        });
                        const recent = (recentRes.data.videosByStatus.items || [])
                            .filter(v => !selected.find(s => s.id === v.id))
                            .map(v => ({ ...v, _onThisDay: false }));
                        selected.push(...recent);
                    } catch (e) {
                        console.error('FeaturedCarousel: error fetching recent videos fallback', e);
                    }
                }

                // Keep significant-date slides up front; shuffle the rest so the
                // fallback order varies between visits.
                const significantSlides = selected.filter(s => s._onThisDay || s._nearDate);
                const otherSlides = selected.filter(s => !s._onThisDay && !s._nearDate);
                this.shuffleArray(otherSlides);

                // Cap at 8 slides
                this.slides = [...significantSlides, ...otherSlides].slice(0, 8);

                if (this.slides.length > 1) {
                    this.startAutoplay();
                }
            } catch (error) {
                console.error('FeaturedCarousel: error fetching slides', error);
                this.slides = [];
            } finally {
                this.loading = false;
            }
        },
        async fetchSignificantVideos() {
            const matches = [];
            let nextToken = null;
            let pages = 0;
            const isAdmin = this.isAdminViewer;
            const myOwnerId = this.myOwnerId;
            const loggedIn = isAdmin || !!myOwnerId;

            // significantDate is unindexed and sparse; paginate the ready partition
            // for rows that have a significantDate set (all dates contain "-").
            // Public visitors get a server-side published filter so drafts aren't
            // shipped to them. Owners/admins fetch all and filter by role locally,
            // so their draft "on this day" videos surface too.
            const filter = loggedIn
                ? { significantDate: { contains: '-' } }
                : { publishState: { eq: 'published' }, significantDate: { contains: '-' } };

            do {
                const res = await API.graphql({
                    query: videosByStatusWithOwner,
                    variables: { status: 'ready', filter, limit: 1000, nextToken }
                });
                const conn = res.data.videosByStatus;
                matches.push(...(conn.items || []));
                nextToken = conn.nextToken;
                pages += 1;
            } while (nextToken && pages < 10);

            if (isAdmin) return matches;
            if (myOwnerId) {
                return matches.filter(v =>
                    v.publishState === 'published' || (v.owner && v.owner.id === myOwnerId));
            }
            return matches;
        },
        // 1-based day-of-year for a month/day on a fixed non-leap calendar;
        // only used for relative distances, so leap-day drift is irrelevant.
        dayOfYear(month, day) {
            const DAYS_BEFORE_MONTH = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
            return (DAYS_BEFORE_MONTH[month - 1] || 0) + day;
        },
        onThisDayLabel(slide) {
            const parts = (slide.significantDate || '').split('-');
            const lead = slide._nearDate
                ? (this.currentLanguage === 'TA' ? 'இந்த நாட்களில்' : 'Around this time')
                : (this.currentLanguage === 'TA' ? 'இந்த நாளில்' : 'On this day');
            if (parts.length < 3) {
                return lead;
            }
            const [year, month, day] = parts;
            const months = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
            const monthName = months[parseInt(month, 10) - 1] || '';
            const dateStr = `${monthName} ${parseInt(day, 10)}, ${year}`;
            return `${lead} · ${dateStr}`;
        },
        backgroundStyle(slide) {
            const url = this.thumbnailUrl(slide);
            return {
                backgroundImage: url ? `url(${url})` : 'none'
            };
        },
        thumbnailUrl(video) {
            if (video.thumbnailUrl && video.thumbnailUrl.trim()) {
                return `https://cdn.eelam.tv/${video.thumbnailUrl}`;
            }
            const imageIndex = video.imageName || '0';
            return `https://cdn1.eelam.tv/public/thumbs/${video.id}_${imageIndex}.jpeg`;
        },
        ownerAvatar(owner) {
            if (owner && owner.legacyAvatar) {
                if (owner.legacyAvatar.startsWith('public/')) {
                    return `https://cdn1.eelam.tv/${owner.legacyAvatar}`;
                }
                return `https://cdn.eelam.tv/${owner.legacyAvatar}`;
            }
            const userId = owner?.id || 'unknown';
            return `https://cdn1.eelam.tv/public/images/user/${userId}/avatar.png`;
        },
        onAvatarError(e) {
            e.target.style.display = 'none';
        },
        slideTitle(slide) {
            if (this.currentLanguage === 'TA' && slide.titleTA) {
                return slide.titleTA;
            }
            return slide.titleEN || slide.titleTA || '';
        },
        startAutoplay() {
            this.clearTimer();
            this.autoplayTimer = setInterval(() => {
                this.currentIndex = (this.currentIndex + 1) % this.slides.length;
            }, 6000);
        },
        clearTimer() {
            if (this.autoplayTimer) {
                clearInterval(this.autoplayTimer);
                this.autoplayTimer = null;
            }
        },
        pause() {
            this.clearTimer();
        },
        resume() {
            if (this.slides.length > 1) {
                this.startAutoplay();
            }
        },
        prev() {
            this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
            this.restartAutoplay();
        },
        next() {
            this.currentIndex = (this.currentIndex + 1) % this.slides.length;
            this.restartAutoplay();
        },
        goTo(index) {
            this.currentIndex = index;
            this.restartAutoplay();
        },
        restartAutoplay() {
            if (this.slides.length > 1) {
                this.clearTimer();
                this.startAutoplay();
            }
        },
        shuffleArray(arr) {
            for (let i = arr.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
            }
        },
        formatViews(count) {
            const label = this.currentLanguage === 'TA' ? 'பார்வைகள்' : 'views';
            if (!count) return `0 ${label}`;
            if (count >= 1000000) return (count / 1000000).toFixed(1) + `M ${label}`;
            if (count >= 1000) return (count / 1000).toFixed(1) + `K ${label}`;
            return count + ` ${label}`;
        },
        formatLikes(count) {
            if (!count) return '0';
            if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M';
            if (count >= 1000) return (count / 1000).toFixed(1) + 'K';
            return String(count);
        }
    }
};
</script>

<style scoped>
.featured-carousel {
    position: relative;
    width: 100%;
    overflow: hidden;
    border-radius: 8px;
    margin-bottom: 24px;
    aspect-ratio: 32 / 9;
    background: var(--bg-tertiary);
}

.carousel-track {
    display: flex;
    transition: transform 0.5s ease;
    height: 100%;
}

.carousel-slide {
    min-width: 100%;
    position: relative;
    overflow: hidden;
}

.slide-bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    /* Thumbnails are 16:9 in a 32:9 hero, so cover crops vertically;
       bias toward the top so faces stay in frame. */
    background-position: center 25%;
    filter: blur(2px);
    transform: scale(1.05);
}

.slide-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(
        to right,
        rgba(0, 0, 0, 0.75) 0%,
        rgba(0, 0, 0, 0.5) 50%,
        rgba(0, 0, 0, 0.2) 100%
    );
}

.slide-content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 40px 80px;
    color: #ffffff;
    z-index: 1;
    max-width: 50%;
}

.slide-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 19, 20, 0.9);
    color: #ffffff;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 16px;
    width: fit-content;
    letter-spacing: 0.3px;
}

.slide-channel .verified-badge-inline {
    color: #ff1314;
    margin-left: -4px;
    display: inline-flex;
}

.slide-channel {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    font-size: 14px;
    opacity: 0.9;
}

.slide-channel-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.4);
}

.slide-title {
    font-size: 28px;
    font-weight: 700;
    line-height: 1.3;
    margin: 0 0 20px 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.slide-play-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--accent-primary);
    color: #ffffff;
    padding: 10px 24px;
    border-radius: 6px;
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
    width: fit-content;
    transition: background 0.2s, transform 0.2s;
}

.slide-play-btn:hover {
    background: var(--accent-hover);
    transform: translateY(-1px);
}

.slide-stats {
    display: flex;
    align-items: center;
    gap: 18px;
    margin-top: 14px;
    opacity: 0.8;
    font-size: 13px;
}

.slide-stat {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

/* Controls */
.carousel-prev,
.carousel-next {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.4);
    color: #ffffff;
    border: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s;
    z-index: 2;
    line-height: 1;
}

.carousel-prev:hover,
.carousel-next:hover {
    background: rgba(0, 0, 0, 0.7);
}

.carousel-prev {
    left: 16px;
}

.carousel-next {
    right: 16px;
}

/* Dots */
.carousel-dots {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    z-index: 2;
}

.carousel-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.6);
    background: transparent;
    cursor: pointer;
    padding: 0;
    transition: background 0.2s, border-color 0.2s;
}

.carousel-dot.active {
    background: #ffffff;
    border-color: #ffffff;
}

.carousel-dot:hover {
    border-color: #ffffff;
}

/* Responsive */
@media (max-width: 992px) {
    .featured-carousel {
        aspect-ratio: 21 / 9;
    }

    .slide-content {
        padding: 30px 40px;
        max-width: 70%;
    }

    .slide-title {
        font-size: 22px;
    }
}

@media (max-width: 576px) {
    .featured-carousel {
        aspect-ratio: 16 / 9;
    }

    .slide-content {
        padding: 20px 24px;
        max-width: 85%;
    }

    .slide-title {
        font-size: 18px;
        -webkit-line-clamp: 2;
        margin-bottom: 14px;
    }

    .slide-badge {
        font-size: 11px;
        padding: 4px 10px;
        margin-bottom: 10px;
    }

    .slide-channel {
        font-size: 12px;
        margin-bottom: 8px;
    }

    .slide-channel-avatar {
        width: 24px;
        height: 24px;
    }

    .slide-play-btn {
        padding: 8px 18px;
        font-size: 13px;
    }

    .slide-stats {
        gap: 12px;
        margin-top: 10px;
        font-size: 11px;
    }

    .carousel-prev,
    .carousel-next {
        width: 32px;
        height: 32px;
        font-size: 20px;
    }

    .carousel-prev {
        left: 8px;
    }

    .carousel-next {
        right: 8px;
    }

    .carousel-dots {
        bottom: 10px;
    }

    .carousel-dot {
        width: 8px;
        height: 8px;
    }
}

/* Shimmer loading skeleton */
.shimmer-carousel {
    background: var(--bg-tertiary);
}

.shimmer-slide {
    position: relative;
    width: 100%;
    height: 100%;
}

.shimmer-slide-bg {
    position: absolute;
    inset: 0;
    background: var(--bg-tertiary);
    overflow: hidden;
}

.shimmer-slide-content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 40px 60px;
    max-width: 50%;
    z-index: 1;
}

.shimmer-badge {
    width: 180px;
    height: 28px;
    border-radius: 20px;
    background: var(--bg-hover);
    overflow: hidden;
    position: relative;
    margin-bottom: 16px;
}

.shimmer-channel-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
}

.shimmer-ch-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--bg-hover);
    overflow: hidden;
    position: relative;
    flex-shrink: 0;
}

.shimmer-ch-name {
    width: 120px;
    height: 14px;
    border-radius: 4px;
    background: var(--bg-hover);
    overflow: hidden;
    position: relative;
}

.shimmer-title-line {
    width: 100%;
    height: 22px;
    border-radius: 4px;
    background: var(--bg-hover);
    overflow: hidden;
    position: relative;
    margin-bottom: 10px;
}

.shimmer-title-line.short {
    width: 60%;
}

.shimmer-btn {
    width: 140px;
    height: 40px;
    border-radius: 6px;
    background: var(--bg-hover);
    overflow: hidden;
    position: relative;
    margin-top: 10px;
}

.shimmer-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
        90deg,
        transparent 0%,
        var(--bg-hover, rgba(255, 255, 255, 0.08)) 50%,
        transparent 100%
    );
    animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
    0% {
        transform: translateX(-100%);
    }
    100% {
        transform: translateX(100%);
    }
}

@media (max-width: 992px) {
    .shimmer-slide-content {
        padding: 30px 40px;
        max-width: 60%;
    }
}

@media (max-width: 576px) {
    .shimmer-slide-content {
        padding: 20px 24px;
        max-width: 80%;
    }

    .shimmer-badge {
        width: 140px;
        height: 22px;
    }

    .shimmer-title-line {
        height: 16px;
    }

    .shimmer-btn {
        width: 120px;
        height: 34px;
    }
}
</style>
